"use client";
import { useRef, useState } from "react";
import { api, commandId } from "../../lib/api";
import { useSession } from "../../lib/session";
import { roleLabel, ROLE_LABEL } from "../console-shell";
import { Banner, Field, FieldGrid, Input, Textarea, Facts, Fact } from ".";
import { Choice, ReadState, useRead } from "./record-tools";
import { flatten, type OrgNode } from "./organization-model";
import { AccessAction } from "./access-action";
import {
  BASIS_LABEL,
  GRANT_LABEL,
  grantState,
  localTime,
  scopeText,
  type AccessEditor,
  type Account,
  type Authority,
  type Candidate,
  type Grant,
  POST_LABEL
} from "./access-model";

export function AccessEditorForm({
  editor,
  onCancel,
  onSaved
}: {
  editor: AccessEditor;
  onCancel: () => void;
  onSaved: (result: unknown) => void;
}) {
  const { whoami } = useSession();
  const g = editor.kind === "grant" ? editor.record : undefined,
    gap = editor.kind !== "password" ? editor.gap : undefined;
  const [initial] = useState(() => ({
    displayName: gap?.personName ?? "",
    username: "",
    password: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    reason: "",
    accountId: editor.kind === "grant" ? (editor.accountId ?? gap?.accountId ?? "") : "",
    organizationId: g?.scopeOrganizationIds[0] ?? gap?.organizationId ?? "",
    roleCode: "ACTIVITY_COORDINATOR",
    grantBasis: gap ? "APPOINTMENT" : "STAFF_ASSIGNMENT",
    basisAppointmentId: gap?.appointmentId ?? "",
    scopeMode: "SUBTREE_DYNAMIC",
    validFrom: "",
    validTo:
      g && editor.action === "delegate"
        ? localTime(
            new Date(
              Math.min(Date.now() + 90 * 86400000, g.validTo ? Date.parse(g.validTo) : Infinity)
            )
          )
        : ""
  }));
  const [form, setForm] = useState(initial),
    [checked, setChecked] = useState(false),
    [accountSearch, setAccountSearch] = useState("");
  const set = (key: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setChecked(false);
  };
  const choosing =
    editor.kind === "grant" && (editor.action === "issue" || editor.action === "delegate");
  const candidates = useRead<{ items: Candidate[] }>(
    choosing ? "/api/v1/grants/assignable-accounts" : null
  );
  const options =
    candidates.data?.items.filter(
      (a) => editor.action !== "delegate" || a.id !== whoami?.accountId
    ) ?? [];
  const selected = options.find((a) => a.id === form.accountId);
  const generalIssue = editor.kind === "grant" && editor.action === "issue" && !gap;
  const organizations = useRead<{ items: OrgNode[] }>(generalIssue ? "/api/v1/orgs/tree" : null);
  const appointmentOptions = useRead<{ items: import("./access-model").Gap[] }>(generalIssue && form.grantBasis === "APPOINTMENT" ? "/api/v1/grants/appointment-gaps" : null);
  const basis = gap ?? appointmentOptions.data?.items.find((a) => a.appointmentId === form.basisAppointmentId);
  const issueOrg = form.grantBasis === "APPOINTMENT" ? basis?.organizationId : form.organizationId;
  const issueOrgName = form.grantBasis === "APPOINTMENT" ? basis?.organizationName : gap?.organizationName ?? flatten(organizations.data?.items ?? []).find(({ node }) => node.id === form.organizationId)?.node.name;
  const issuing = editor.kind === "grant" && editor.action === "issue";
  const shortening = editor.kind === "grant" && editor.action === "shorten";
  const writeCommand = useRef<{ fingerprint: string; id: string } | null>(null);
  const strong = (s: string) =>
    s.length >= 12 && /[A-Z]/.test(s) && /[a-z]/.test(s) && /\d/.test(s);
  const passwordChange = editor.kind === "password",
    credential =
      editor.kind === "account" && (editor.action === "create" || editor.action === "bind");
  const target =
    editor.kind === "account" ? editor.record?.displayName : (gap?.personName ?? g?.accountName);
  const titles = {
    create: "开通工作台账号",
    bind: "新增登录方式",
    enable: "启用账号",
    disable: "停用账号",
    unbind: "解绑登录方式",
    issue: gap ? "按任职签发授权" : "签发授权",
    shorten: "缩短有效期",
    review: "复核授权",
    revoke: editor.kind === "grant" && editor.own ? "收回转授" : "撤销授权",
    delegate: "转授给经办人",
    password: "修改我的口令"
  };
  const invalidTime =
    (choosing || shortening) &&
    !!form.validTo &&
    (!Number.isFinite(Date.parse(form.validTo)) ||
      Date.parse(form.validTo) <=
        (issuing && form.validFrom ? Date.parse(form.validFrom) : Date.now()) ||
      (shortening && g?.validTo && Date.parse(form.validTo) >= Date.parse(g.validTo)) ||
      (editor.action === "delegate" &&
        g?.validTo &&
        Date.parse(form.validTo) > Date.parse(g.validTo)));
  const disabled =
    !checked ||
    (issuing && !!form.validFrom && !Number.isFinite(Date.parse(form.validFrom))) ||
    (issuing && (!issueOrg || !form.roleCode || (form.grantBasis === "APPOINTMENT" && !basis))) ||
    (shortening && !form.validTo) ||
    (choosing && (!selected || !candidates.data)) ||
    (editor.action === "delegate" &&
      (!form.validTo || !g?.scopeOrganizationIds.includes(form.organizationId))) ||
    !!invalidTime ||
    (credential && !strong(form.password)) ||
    (passwordChange &&
      (!strong(form.newPassword) ||
        form.newPassword !== form.confirmPassword ||
        form.newPassword === form.currentPassword));
  const post = (path: string, body?: object) => {
    // Grant commands are not idempotent at the API; AccessAction still requires reading before a retry.
    const tracked = issuing || shortening;
    const fingerprint = JSON.stringify({ path, body });
    if (tracked && writeCommand.current?.fingerprint !== fingerprint) writeCommand.current = { fingerprint, id: commandId() };
    return api(path, { method: "POST", ...(body ? { body: { ...body, ...(tracked ? { commandId: writeCommand.current!.id } : {}) } } : {}) });
  };
  const inspect = async () => {
    if (editor.kind === "account") {
      const data = editor.record
        ? { items: [await api<Account>(`/api/v1/accounts/${editor.record.id}`)] }
        : await api<{ items: Account[] }>(
            `/api/v1/accounts?${new URLSearchParams({ q: form.username, limit: "20" })}`
          );
      return data.items.map(
        (a) =>
          `${a.displayName} · ${a.status === "ACTIVE" ? "在用" : "已停用"} · ${a.identities.map((i) => i.usernameDisplay ?? i.provider).join("、") || "无登录方式"}`
      );
    }
    const data =
      editor.kind === "grant" && editor.own
        ? await api<Authority>("/api/v1/grants/mine")
        : await api<{ items: Grant[] }>(
            `/api/v1/grants/workspace?${new URLSearchParams({ accountId: g?.accountId ?? form.accountId })}`
          );
    const rows = (data as Authority).delegations ?? data.items;
    return rows.map(
      (r) =>
        `${r.accountName} · ${roleLabel(r.roleCode)} · ${GRANT_LABEL[grantState(r)]} · ${scopeText(r)}`
    );
  };
  return (
    <AccessAction
      title={titles[editor.action]}
      description={
        target
          ? `办理对象：${target}`
          : issuing ? "选择接收账号、职责、授权依据和组织范围。"
          : passwordChange
            ? "验证当前口令，设置新的登录口令。"
            : "为工作人员建立自己的登录账号。"
      }
      dirty={JSON.stringify(initial) !== JSON.stringify(form) || checked}
      disabled={disabled}
      danger={["disable", "unbind", "revoke"].includes(editor.action)}
      label={titles[editor.action]}
      onCancel={onCancel}
      onSaved={onSaved}
      inspect={passwordChange ? undefined : inspect}
      submit={async () => {
        if (editor.kind === "account") {
          if (editor.action === "create")
            return post("/api/v1/accounts", {
              displayName: form.displayName.trim(),
              username: form.username.trim(),
              password: form.password
            });
          const base = `/api/v1/accounts/${editor.record!.id}`;
          if (editor.action === "bind")
            return post(`${base}/identities`, {
              username: form.username.trim(),
              password: form.password
            });
          if (editor.action === "unbind")
            return post(`${base}/identities/${editor.identity!.id}/unbind`);
          return post(`${base}/${editor.action}`);
        }
        if (editor.kind === "password")
          return post("/api/v1/auth/password", {
            currentPassword: form.currentPassword,
            newPassword: form.newPassword
          });
        if (editor.action === "issue")
          return post("/api/v1/grants", {
            accountId: form.accountId,
            roleCode: form.roleCode,
            scopeMode: form.scopeMode,
            scopeOrganizationIds: [issueOrg!],
            grantBasis: form.grantBasis,
            ...(form.grantBasis === "APPOINTMENT" ? { basisAppointmentId: basis!.appointmentId } : {}),
            ...(form.validFrom ? { validFrom: new Date(form.validFrom).toISOString() } : {}),
            ...(form.validTo ? { validTo: new Date(form.validTo).toISOString() } : {})
          });
        if (shortening) return post(`/api/v1/grants/${g!.id}/shorten`, { validTo: new Date(form.validTo).toISOString() });
        if (editor.action === "delegate")
          return post(`/api/v1/grants/${g!.id}/delegate`, {
            accountId: form.accountId,
            scopeMode: "EXPLICIT_SET",
            scopeOrganizationIds: [form.organizationId],
            validTo: new Date(form.validTo).toISOString()
          });
        return post(
          `/api/v1/grants/${g!.id}/${editor.action}`,
          editor.action === "revoke" ? { reason: form.reason.trim() } : undefined
        );
      }}
    >
      {editor.kind === "account" && (
        <section className="record-section">
          <h2>{credential ? "账号与登录" : "核对操作影响"}</h2>
          {editor.action === "create" && (
            <Field label="账号显示名">
              <Input
                required
                maxLength={120}
                value={form.displayName}
                onChange={(e) => set("displayName", e.target.value)}
              />
            </Field>
          )}
          {credential && (
            <>
              <FieldGrid>
                <Field label="登录名">
                  <Input
                    required
                    maxLength={120}
                    autoComplete="off"
                    value={form.username}
                    onChange={(e) => set("username", e.target.value)}
                  />
                </Field>
                <Field label="初始口令" hint="至少 12 位，含大写字母、小写字母和数字。">
                  <Input
                    type="password"
                    autoComplete="new-password"
                    required
                    value={form.password}
                    onChange={(e) => set("password", e.target.value)}
                  />
                </Field>
              </FieldGrid>
              <p className="record-muted">
                请向本人交付登录信息，并请其首次登录后修改口令。账号权限需另行签发。
              </p>
              {editor.action === "bind" && (
                <p>原登录方式继续保留。确认新方式可用后，再办理解绑。</p>
              )}
              {gap && (
                <p>
                  此账号用于待授事项「{gap.personName} · {gap.organizationName}
                  」。开户后仍需核对人员与账号，不会自动建立会员身份绑定。
                </p>
              )}
            </>
          )}
          {editor.action === "disable" && (
            <Banner tone="warning">
              停用后，该账号现有会话立即失效，不能继续登录；已办理的事项保留。
            </Banner>
          )}
          {editor.action === "enable" && (
            <p>重新允许该账号登录。已有授权仍按复核状态和有效期判断是否生效。</p>
          )}
          {editor.action === "unbind" && (
            <>
              <div className="access-summary">
                <strong>{editor.identity?.usernameDisplay ?? editor.identity?.provider}</strong>
                <p>这是本次要解绑的登录方式。</p>
              </div>
              <Banner tone="warning">
                解绑后该方式不能继续登录，该账号现有会话全部终止。
                {editor.record?.identities.length === 1
                  ? "这是最后一种登录方式，解绑后需新增登录方式才能再次进入。"
                  : "其他登录方式继续保留。"}
              </Banner>
            </>
          )}
        </section>
      )}
      {editor.kind === "grant" && (
        <>
          <section className="record-section">
            <h2>授权依据与范围</h2>
            {gap ? (
              <Facts>
                <Fact label="任职人员">{gap.personName}</Fact>
                <Fact label="任职依据">
                  {gap.termName} · {POST_LABEL[gap.postCode] ?? gap.postCode}
                </Fact>
                <Fact label="签发职责">{roleLabel(form.roleCode)}</Fact>
                <Fact label="组织范围">{gap.organizationName}及下级</Fact>
              </Facts>
            ) : (
              g && (
                <Facts>
                  <Fact label="授权人员">{g.accountName}</Fact>
                  <Fact label="当前职责">{roleLabel(g.roleCode)}</Fact>
                  <Fact label="组织范围">{scopeText(g)}</Fact>
                  <Fact label="授权依据">
                    {BASIS_LABEL[g.grantBasis]}
                    {g.issuerName ? ` · ${g.issuerName}` : ""}
                  </Fact>
                </Facts>
              )
            )}
            {issuing && <>
              {/* Desktop duties use the existing labels; grant API validates the role definition and adjudicates every issuance. */}
              <Choice label="签发职责" required value={form.roleCode} onChange={(v) => set("roleCode", v)} options={Object.entries(ROLE_LABEL).map(([value, label]) => ({ value, label }))} />
              <Choice label="授权依据" required value={form.grantBasis} onChange={(v) => set("grantBasis", v)} options={[{ value: "STAFF_ASSIGNMENT", label: BASIS_LABEL.STAFF_ASSIGNMENT }, { value: "APPOINTMENT", label: BASIS_LABEL.APPOINTMENT }]} />
              {generalIssue && form.grantBasis === "APPOINTMENT" && <><ReadState {...appointmentOptions} retry={appointmentOptions.reload} /><Choice label="任职依据" required value={form.basisAppointmentId} onChange={(v) => set("basisAppointmentId", v)} options={(appointmentOptions.data?.items ?? []).map((a) => ({ value: a.appointmentId, label: `${a.personName} · ${a.organizationName} · ${POST_LABEL[a.postCode] ?? a.postCode}` }))} /></>}
              {form.grantBasis === "STAFF_ASSIGNMENT" && generalIssue && <><ReadState {...organizations} retry={organizations.reload} /><Choice label="授权组织" required value={form.organizationId} onChange={(v) => set("organizationId", v)} options={flatten(organizations.data?.items ?? []).filter(({ node }) => node.lifecycle !== "ARCHIVED").map(({ node }) => ({ value: node.id, label: node.name }))} /></>}
              {form.grantBasis === "APPOINTMENT" && <p>所属组织：{basis?.organizationName ?? "请先选择任职依据"}。任职授权范围固定为该组织。</p>}
              <Choice label="组织范围方式" value={form.scopeMode} onChange={(v) => set("scopeMode", v)} options={[{ value: "EXPLICIT_SET", label: "仅所选组织" }, { value: "SUBTREE_DYNAMIC", label: "包含下级组织" }]} />
              <p className="record-muted">另一位授权管理员复核后按约定时间生效。签发对象、职责和范围由服务端核对。</p>
            </>}
            {shortening && <><Banner tone="warning">新截止时间须晚于当前时间，且早于原截止时间；未设到期的授权可设置截止时间。</Banner><Field label="新的截止时间"><Input type="datetime-local" required value={form.validTo} onChange={(e) => set("validTo", e.target.value)} /></Field>{invalidTime && <Banner tone="danger">请填写将来的时间，并早于原截止时间。</Banner>}</>}
            {editor.action === "review" && (
              <Banner tone="warning">
                请核对被授权人员、职责、范围及依据。复核后按约定生效时间启用；签发人不能复核自己的签发记录。
              </Banner>
            )}
            {editor.action === "revoke" && (
              <>
                <Banner tone="warning">
                  撤销后该授权失效，依附它的转授一并失效。其他独立授权继续按自身规则生效。
                </Banner>
                <Field label="撤销理由">
                  <Textarea
                    required
                    minLength={1}
                    maxLength={1000}
                    rows={5}
                    value={form.reason}
                    onChange={(e) => set("reason", e.target.value)}
                  />
                </Field>
              </>
            )}
          </section>
          {choosing && (
            <section className="record-section">
              <h2>接收账号与有效期</h2>
              <ReadState {...candidates} retry={candidates.reload} />
              <Field label="查找接收账号" hint="在本次读取的最多 500 个可授账号中查找。">
                <Input
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  placeholder="输入账号显示名"
                />
              </Field>
              <Choice
                label="接收账号"
                value={form.accountId}
                required
                disabled={!candidates.data}
                onChange={(v) => set("accountId", v)}
                options={options
                  .filter(
                    (a) => a.id === form.accountId || a.displayName.includes(accountSearch.trim())
                  )
                  .map((a) => ({ value: a.id, label: a.displayName }))}
              />
              {candidates.data && options.length === 0 && (
                <Banner tone="warning">当前没有可选的接收账号。</Banner>
              )}
              {editor.action === "delegate" && (
                <>
                  <Choice
                    label="转授组织"
                    required
                    value={form.organizationId}
                    onChange={(v) => set("organizationId", v)}
                    options={g!.scopeOrganizations.map((o) => ({ value: o.id, label: o.name }))}
                  />
                  <p className="record-muted">
                    本次仅转授所选组织，职责保持为「{roleLabel(g!.roleCode)}」。接收人不能再次转授。
                  </p>
                </>
              )}
              <FieldGrid>
                {editor.action === "issue" && (
                  <Field label="生效时间" hint="留空表示复核通过后即可生效。">
                    <Input
                      type="datetime-local"
                      value={form.validFrom}
                      onChange={(e) => set("validFrom", e.target.value)}
                    />
                  </Field>
                )}
                <Field
                  label="到期时间"
                  hint={
                    editor.action === "delegate"
                      ? "必须填写，且不能晚于原授权到期时间。"
                      : "留空表示不设到期时间。"
                  }
                >
                  <Input
                    type="datetime-local"
                    required={editor.action === "delegate"}
                    value={form.validTo}
                    onChange={(e) => set("validTo", e.target.value)}
                  />
                </Field>
              </FieldGrid>
              {invalidTime && (
                <Banner tone="danger">
                  {issuing ? "请核对有效期：到期时间须晚于生效时间。" : "请核对有效期：转授到期时间须晚于当前时间，且不能超过原授权期限。"}
                </Banner>
              )}
              <div className="access-summary">
                <strong>接收账号：{selected?.displayName ?? "待选择"}</strong>
                <p>
                  {editor.action === "delegate"
                    ? g?.scopeOrganizations.find((o) => o.id === form.organizationId)?.name
                    : issueOrgName}{" "}
                  · {roleLabel(g?.roleCode ?? form.roleCode)}
                </p>
              </div>
            </section>
          )}
        </>
      )}
      {passwordChange && (
        <section className="record-section">
          <h2>更新登录口令</h2>
          <Field label="当前口令">
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={form.currentPassword}
              onChange={(e) => set("currentPassword", e.target.value)}
            />
          </Field>
          <FieldGrid>
            <Field label="新口令" hint="至少 12 位，含大写字母、小写字母和数字。">
              <Input
                type="password"
                autoComplete="new-password"
                required
                value={form.newPassword}
                onChange={(e) => set("newPassword", e.target.value)}
              />
            </Field>
            <Field label="再次填写新口令">
              <Input
                type="password"
                autoComplete="new-password"
                required
                value={form.confirmPassword}
                onChange={(e) => set("confirmPassword", e.target.value)}
              />
            </Field>
          </FieldGrid>
          {form.confirmPassword && form.confirmPassword !== form.newPassword && (
            <Banner tone="warning">两次填写的新口令不一致。</Banner>
          )}
          <p className="record-muted">
            修改成功后，所有会话（包括当前这一个）都会终止，请用新口令重新登录。
          </p>
        </section>
      )}
      <label className="record-check">
        <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
        {passwordChange ? "我已记住新口令，确认修改后重新登录" : "我已核对办理对象、范围及上述影响"}
      </label>
    </AccessAction>
  );
}
